// utils/validationUtils.ts

/** Converte caractere alfanumérico para valor numérico para cálculo de DV do CNPJ */
const cnpjCharValue = (c: string): number => {
    const code = c.toUpperCase().charCodeAt(0);
    if (code >= 48 && code <= 57) return code - 48; // '0'-'9'
    return code - 65 + 10; // 'A'=10, 'B'=11, ..., 'Z'=35
};

/** Aplica máscara de CPF: 000.000.000-00 */
export const maskCPF = (v: string): string => {
    v = v.replace(/\D/g, '').substring(0, 11);
    const n = v.length;
    if (n <= 3) return v;
    if (n <= 6) return `${v.slice(0, 3)}.${v.slice(3)}`;
    if (n <= 9) return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
    return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
};

/** Aplica máscara de CNPJ: 00.000.000/0000-00 (suporta CNPJ alfanumérico) */
export const maskCNPJ = (v: string): string => {
    v = v.replace(/[^A-Z0-9]/gi, '').toUpperCase().substring(0, 14);
    const n = v.length;
    if (n <= 2) return v;
    if (n <= 5) return `${v.slice(0, 2)}.${v.slice(2)}`;
    if (n <= 8) return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5)}`;
    if (n <= 12) return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8)}`;
    return `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12)}`;
};

/**
 * Aplica máscara de CPF ou CNPJ com base no conteúdo.
 * Detecta CNPJ quando o valor tem mais de 11 caracteres alfanuméricos ou contém letras.
 */
export const maskCpfCnpj = (input: string): string => {
    const raw = input.replace(/[^A-Z0-9]/gi, '');
    if (raw.length > 11 || /[A-Z]/i.test(raw)) {
        return maskCNPJ(input);
    }
    return maskCPF(input);
};

export const formatCpfOrCnpj = (value: string): string => {
    const cleanValue = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();

    if (cleanValue.length === 11 && !/[A-Z]/.test(cleanValue)) {
        return maskCPF(cleanValue);
    } else if (cleanValue.length === 14) {
        return maskCNPJ(cleanValue);
    } else {
        return 'Valor inválido para CPF ou CNPJ';
    }
}
 
export const formatCep = (cep: string): string => {
    // Remove qualquer caractere que não seja dígito
    const cleanCep = cep.replace(/\D/g, '');
 
    // Verifica se o CEP tem o tamanho correto (8 dígitos)
    if (cleanCep.length !== 8) {
        return 'CEP inválido';
    }
 
    // Formata o CEP no formato 00000-000
    return cleanCep.replace(/(\d{5})(\d{3})/, '$1-$2');
}
 
export const formatPhoneNumber = (phoneNumber: string): string => {
    const cleanedPhoneNumber = phoneNumber.replace(/\D/g, ''); // Remove todos os caracteres não numéricos
 
    // Verifica se o número tem o formato válido de 10 ou 11 dígitos
    if (cleanedPhoneNumber.length === 10) {
        return cleanedPhoneNumber.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3'); // Formato (XX) XXXX-XXXX
    } else if (cleanedPhoneNumber.length === 11) {
        return cleanedPhoneNumber.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3'); // Formato (XX) XXXXX-XXXX
    }
 
    return phoneNumber;
};
 
export const formatDefaultValuesToString = (value: any): string => {
    if (value === null || value === undefined) return "-";
 
    if (typeof value === "boolean") return value ? "Sim" : "Não";
 
    if (Array.isArray(value)) return renderArray(value);
 
    if (typeof value === "object" && !Array.isArray(value))
        return Object.values(value).join(" - ");
 
    if (isDateValue(value))
        return formatDate(value);
 
    return value.toString();
};
 
export const formatSpecificValuesToString = (key: any, value: any): string => {
    if (value === null || value === undefined) return "-";
 
    if (typeof value === "boolean") return value ? "Sim" : "Não";
 
    if (Array.isArray(value)) return renderArray(value);
 
    if (typeof value === "object" && !Array.isArray(value))
        return formatObjectValues(key, value);
 
    if (isDateValue(value)) {
        if (key === "idade") return calculateAgeToString(value);
        return formatDate(value);
    }
 
    return formatNonObjectValues(key, value);
};
 
const formatNonObjectValues = (key: any, value: any): string => {
    switch (key) {
        case "valor":
        case "valorPendencia":
        case "valorTotal":
        case "valorUltimaOcorrencia":
            return formatValueReais(value);
 
        case "CEP":
            return formatCep(value.toString());
 
        case "DOCUMENTO":
        case "CPFCNPJ":
            return formatCpfOrCnpj(value);
 
        default:
            return value.toString();
    }
};
 
const formatObjectValues = (key: any, value: any): string => {
    switch (key) {
        case "protocolo":
            return [
                `Número: ${value.numero || "N/A"}`,
                `Digito: ${value.digito || "N/A"}`,
            ].join(" - ");
 
        case "operador":
            return [
                `Código: ${value.codigo || "N/A"}`,
                `Nome: ${value.nome || "N/A"}`,
            ].join(" - ");
 
        case "endereco":
            const addressParts = [
                value.logradouro,
                value.numero,
                value.bairro,
                value.complemento,
                `${value.cidade?.estado?.siglaUf || ""} ${value.cidade?.nome || ""}`,
                formatCep(value.cep.toString()),
            ];
            return addressParts
                .filter((part) => part?.toString().trim())
                .join(" - ");
 
        case "telefoneCelular":
        case "telefoneResidencial":
        case "telefoneAssociado":
            return formatPhoneNumber(`${value.numeroDdd || ""}${value.numero || ""}`.trim());
 
        case "situacaoCpf":
            return [
                `Descrição Situação: ${value.descricaoSituacao || "N/A"}`,
                `UF: ${value.uf || "N/A"}`,
                `Data: ${value.dataSituacao ? formatDate(value.dataSituacao) : "N/A"}`,
            ].join(" - ");
 
        case "cpf":
            return [
                `CPF: ${value.numero ? formatCpfOrCnpj(value.numero) : "N/A"}`,
                `Região Origem: ${value.regiaoOrigem || "N/A"}`,
            ].join(" - ");
 
        case "origemAssociado":
        case "cidadeAssociado":
        case "cidade":
            return [
                `${value.estado?.siglaUf || "N/A"}`,
                `${value.nome || "N/A"}`,
            ].join(" - ");
 
        case "moeda":
            return value.simbolo || "-";
 
        default:
            return JSON.stringify(value);
    }
};
 
export const isDateValue = (value: any): boolean => {
    if (value instanceof Date)
        return !isNaN(value.getTime());
 
    if (typeof value === "string") {
        const parsedDate = new Date(value);
        return !isNaN(parsedDate.getTime()) && /^\d{4}-\d{2}-\d{2}/.test(value);
    }
 
    return false;
};
 
export const formatValueReais = (valor: number | string | null | undefined): string => {
    if (valor === null || valor === undefined || (typeof valor === "string" && !valor.trim())) 
        return "R$ 0,00";
 
    let numericValue: number;
 
    if (typeof valor === "string") {
        const valorSemVirgula = valor.replace(",", ".");
        numericValue = parseFloat(valorSemVirgula);
 
        if (isNaN(numericValue)) 
            return "R$ 0,00";
    } else 
        numericValue = valor;
 
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    }).format(numericValue);
};
 
export const formatDate = (data: any): string => {
    const date = new Date(data);
    const options: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
    };
 
    return date.toLocaleDateString("pt-BR", options);
};
 
export const calculateAgeToString = (dateOfBirth: any): string => {
    const birth = new Date(dateOfBirth);
    const today = new Date();
 
    let age = today.getFullYear() - birth.getFullYear();
    const month = today.getMonth() - birth.getMonth();
 
    // Se o mês atual for anterior ao mês de nascimento, ou for o mês de nascimento, mas o dia atual ainda não passou, diminui um ano
    if (month < 0 || (month === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
 
    return age.toString();
};
 
export const renderArray = (array: any[]): string => {
    return array
        .filter((item) => item !== null && item.toString().trim() !== "")
        .map((item) => (typeof item === "object" ? JSON.stringify(item) : item.toString()))
        .join(", ");
};
 
export const isValidRenavam = (renavam: string) => {
 
    let d: any = renavam.split("");
    let soma: number = 0,
        valor = 0,
        digito = 0,
        x = 0;
 
    for (var i = 5; i >= 2; i--) {
        soma += d[x] * i;
        x++;
    }
 
    valor = soma % 11;
 
    if (valor == 11 || valor == 0 || valor >= 10) {
        digito = 0;
    } else {
        digito = valor;
    }
 
    if (digito == d[4]) {
        return true;
    } else {
        return false;
    }
}
 
// Validação de CPF
export const isValidCPF = (cpf: string): boolean => {
    cpf = cpf.replace(/[^\d]+/g, ''); // Remove caracteres não numéricos
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
        return false; // CPF inválido se não tiver 11 dígitos ou todos os números forem iguais
    }
 
    let sum: number;
    let rest: number;
    sum = 0;
 
    for (let i = 1; i <= 9; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
    }
 
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(cpf.substring(9, 10))) return false;
 
    sum = 0;
    for (let i = 1; i <= 10; i++) {
        sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
    }
 
    rest = (sum * 10) % 11;
    if (rest === 10 || rest === 11) rest = 0;
    if (rest !== parseInt(cpf.substring(10, 11))) return false;
 
    return true;
};
 
// Validação de CNPJ (suporta CNPJ alfanumérico — Receita Federal 2026)
export const isValidCNPJ = (cnpj: string): boolean => {
    cnpj = cnpj.replace(/[^A-Z0-9]/gi, '').toUpperCase();

    if (cnpj.length !== 14) return false;

    // Rejeita sequências com todos os caracteres iguais
    if (/^(.)\1+$/.test(cnpj)) return false;

    // Calcula primeiro dígito verificador
    let tamanho = 12;
    let soma = 0;
    let pos = tamanho - 7; // 5
    for (let i = tamanho; i >= 1; i--) {
        soma += cnpjCharValue(cnpj.charAt(tamanho - i)) * pos--;
        if (pos < 2) pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== cnpjCharValue(cnpj.charAt(12))) return false;

    // Calcula segundo dígito verificador
    tamanho = 13;
    soma = 0;
    pos = tamanho - 7; // 6
    for (let i = tamanho; i >= 1; i--) {
        soma += cnpjCharValue(cnpj.charAt(tamanho - i)) * pos--;
        if (pos < 2) pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== cnpjCharValue(cnpj.charAt(13))) return false;

    return true;
};
 
// Validação de e-mail
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isValidEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email);
};

// Validação de um ou mais e-mails separados por ponto e vírgula (;)
// Qualquer outro separador (vírgula, espaço, etc.) invalida o campo.
export const isValidMultiEmail = (value: string): boolean => {
    if (isEmpty(value)) return false;

    const emails = value.split(';').map((e) => e.trim());
    if (emails.some((e) => e === '')) return false;

    return emails.every((e) => EMAIL_REGEX.test(e));
};

// Validação de campo vazio
export const isEmpty = (value: string | null | undefined): boolean => {
    return value === null || value === undefined || value.trim() === '';
};
 
export const openBase64Data = (base64Data: string, dataType: string) => {
    if (!base64Data) return; // Evita erro se o campo estiver vazio
  
    const byteCharacters = atob(base64Data);
    const byteArrays = [];
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArrays.push(byteCharacters.charCodeAt(i));
    }
    const byteArray = new Uint8Array(byteArrays);
    const blob = new Blob([byteArray], { type: dataType });
  
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
};